<template>
  <PageContainer @click.capture="guardManageAction">
    <View direction="column" gap="sm">
      <PageBackNav :teleport-slot-id="headerTeleportSlot" @back-nav="goBack(showingsRoute())" />

      <View v-if="state.status === 'pending'" justify="center" padding="xl">
        <LoadingIndicator />
      </View>

      <EmptyPagePlaceholder
        v-else-if="state.status === 'error'"
        title="Could not load showing"
        description="Please reload the page or try again later"
      />

      <div v-else-if="showing" class="wrapper">
        <Card class="showing-card">
          <template #header>
            <View
              justify="space-between"
              :direction="screenMinSm ? 'row' : 'column'"
              :gap="screenMinSm ? 'md' : 'sm'"
              :grow="1"
            >
              <Text variant="h2">{{ title }}</Text>
              <TourTypeLabel :tour-type="showing.tourType" />
            </View>
          </template>

          <View direction="column" gap="md">
            <Link :to="propertyLink" :decoration="false" class="property-link">
              <View :grow="1" gap="sm" align="center">
                <PropertyPhotoPreview :image-url="showing.property.coverImage" size="sm" />
                <Text variant="label-medium">{{ showing.property.displayName }}</Text>
              </View>
            </Link>

            <ShowingRenters :showing="showing" />
          </View>
        </Card>

        <!-- Without an agent the card offers to add one, which only makes sense while the showing can still happen -->
        <ShowingAgentCard
          v-if="showingAgent || isEditable"
          :showing-agent="showingAgent"
          :showing-id="showing.id"
          :tour-type="showing.tourType"
          :property-id="showing.property?.id"
        />

        <Card class="controls-card">
          <View gap="sm" class="controls">
            <Button variant="secondary" :disabled="!isEditable" data-manage-action @click="openRescheduleSrDialog">
              Reschedule
            </Button>
            <Button
              variant="secondary"
              :disabled="!isEditable"
              appearance="danger"
              data-manage-action
              @click="openCancelSrDialog"
            >
              Cancel showing
            </Button>
          </View>
        </Card>

        <Card class="aside">
          <template #header>
            <Text variant="h4">New showing requests</Text>
          </template>

          <View direction="column" gap="sm" v-if="pendingShowingRequests?.length">
            <ShowingRequestReviewCard
              v-for="showingRequest in pendingShowingRequests"
              :key="showingRequest.id"
              :renter-name="showingRequest.renter.user.name"
              :renter-id="showingRequest.renter.id"
              :property-id="showing.property?.id"
              :requested-at="showingRequest.createdAt"
            />
          </View>
          <Text v-else variant="body-medium" color="secondary">We will show new requests for this showing here</Text>
        </Card>

        <!-- Dialogs-->
        <CancelShowingModal
          :showing-id="showing.id"
          :renter-label="renterLabel"
          ref="cancelDialogRef"
          @reschedule="openRescheduleSrDialog"
        />
        <RescheduleShowingModal
          :showing-id="showing.id"
          :property-id="showing.property?.id"
          :showing-agent-id="showing.showingAgent?.id"
          :current-start-time="showing.startTime"
          :tour-type="showing.tourType"
          ref="rescheduleDialogRef"
        />
      </div>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, ref } from 'vue';

import {
  Button,
  Card,
  EmptyPagePlaceholder,
  Link,
  LoadingIndicator,
  PageBackNav,
  PageContainer,
  screenMinSm,
  Text,
  View,
} from '@tallo/design-system';
import { propertyDashboardRoute, showingsRoute, useNavigation } from '@tallo/investor/navigation';
import { useManageActionGuard } from '@tallo/investor/property';
import {
  ShowingRequestStatus,
  CancelShowingModal,
  getShowingTime,
  RescheduleShowingModal,
  ShowingStatus,
  useShowingQuery,
  ShowingRequestReviewCard,
  TourTypeLabel,
} from '@tallo/investor/showing';
import { PropertyPhotoPreview } from '@tallo/property';

import { headerTeleportSlot } from '../../../root/header-teleport-slot.const';
import ShowingAgentCard from './showing-agent-card.vue';
import ShowingRenters from './showing-renters.vue';

const { goBack } = useNavigation();

const rescheduleDialogRef = ref<typeof RescheduleShowingModal | null>(null);
const cancelDialogRef = ref<typeof CancelShowingModal | null>(null);

const { state } = useShowingQuery();

const showing = computed(() => state.value.data);
// Rescheduling, cancelling or changing the host: stopped for a colleague who can only view the unit
const guardManageAction = useManageActionGuard(() => showing.value?.property.id);
const showingAgent = computed(() => showing.value?.showingAgent);
const propertyLink = computed(() => propertyDashboardRoute(showing.value?.property.id!));
const isEditable = computed(() => [ShowingStatus.PENDING, ShowingStatus.CONFIRMED].includes(showing.value?.status!));
const pendingShowingRequests = computed(() => {
  return showing.value?.showingRequests?.filter(({ status }) => status === ShowingRequestStatus.PENDING);
});

// Canceling a showing terminates every renter still attached to the slot, so the modal has to name all of them
const renterLabel = computed(() => {
  const affectedRenters =
    showing.value?.showingRequests?.filter(({ status }) =>
      [ShowingRequestStatus.PENDING, ShowingRequestStatus.ACCEPTED].includes(status),
    ) ?? [];

  if (affectedRenters.length === 1) {
    return affectedRenters[0].renter.user.name.split(' ')[0];
  }

  return affectedRenters.length > 1 ? `these ${affectedRenters.length} renters` : 'this renter';
});

const title = computed(() => {
  const startTime = showing.value?.startTime;
  const endTime = showing.value?.endTime;

  if (!startTime || !endTime) {
    return '';
  }

  return `${useDateFormat(startTime, 'dddd').value}, ${getShowingTime(startTime, endTime, 'comma')}`;
});

function openRescheduleSrDialog() {
  rescheduleDialogRef.value!.open();
}

function openCancelSrDialog() {
  cancelDialogRef?.value!.open();
}
</script>

<style lang="scss" scoped>
@import 'styles/mixins/layout.mixins';

.wrapper {
  display: grid;
  grid-template-columns: auto 30rem;
  gap: var(--t-spacing-sm);

  @include screen-max-lg {
    grid-template-columns: 1fr;
    max-width: 100%;
  }
}

.aside {
  width: 100%;
  grid-area: 1 / 2 / 3 / 3;

  @include screen-max-lg {
    grid-area: 2;
  }
}

.controls {
  & > * {
    flex-grow: 1;
  }

  @include mobile {
    flex-direction: column;
    justify-content: end;
  }
}

.property-link {
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
}
</style>
